import API_BASE from "../config.js";
import React, { useState, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Nebulosa from "./Nebulosa";
// Acabamento visual (vidro/3D). So injeta uma tag <style>: nao altera
// estrutura, rota, handler nem item de menu -- ver EstiloGlobal.js.
import EstiloGlobal from "./EstiloGlobal";
// Lembretes do que precisa de atencao hoje, em qualquer tela. Sobrepoe o
// conteudo (position:fixed): nenhuma pagina muda de largura. Sem nada
// pendente ele nao renderiza nada -- ver LembretesLaterais.js.
import LembretesLaterais from "./LembretesLaterais";
import useIsMobile from "../hooks/useIsMobile";
// AUD-E: o segmento da oficina ja chegava nesta tela dentro da resposta de
// oficina_config_<tenant> e era descartado. Guardar e o que leva "moto"
// para Clientes, O.S., Agendamentos, Orcamentos e Dashboard.
import { gravarSegmento } from "../i18n/segmento";
// Extrai tenant_id do JWT token - evita conflito entre tenants
function getTenantIdFromToken() {
  try {
    const token = (sessionStorage.getItem("access_token")||localStorage.getItem("access_token"));
    if (!token) return (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return String(payload.tenant_id || (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1");
  } catch { return (sessionStorage.getItem("tenant_id")||localStorage.getItem("tenant_id")) || "1"; }
}
function getConfigKey() {
  try {
    const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
    if (!token) return "saas_config";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return "saas_config_tenant_" + (payload.tenant_id || "1");
  } catch { return "saas_config"; }
}
// Correcao pontual -- vazamento visual entre tenants: CONFIG_KEY era uma
// constante fixada UMA VEZ (no primeiro carregamento do modulo JS), entao
// se o usuario trocasse de conta/tenant sem um reload completo da pagina,
// continuava lendo/escrevendo no cache local do tenant antigo. Agora e
// recalculada a cada vez que e usada (getConfigKey() chamado direto).
const CONFIG_API  = "" + API_BASE + "/api/v1/config";
const TENANT_API  = "" + API_BASE + "/api/v1/tenants";
function getTenantId() {
  return parseInt(getTenantIdFromToken() || "1");
}

// licenca_tenant_/oficina_config_ carregam dado de uma oficina especifica e
// agora exigem autenticacao no backend (ver auditoria de 11/08/2026 --
// essas chaves vazavam sem checagem de tenant). Sem este header as chamadas
// abaixo passam a voltar 401.
//
// AUD-S2 (10/10/2026): a frase que havia aqui -- "tema_oficina_ continua
// publica de proposito (lida pela tela de Login antes de existir token)"
// -- estava DESATUALIZADA. O inventario deste AUD mostrou que Login.js nao
// le config nenhuma; quem le tema_oficina_ e este Layout, que roda depois
// do login (a chamada de licenca_tenant_ logo abaixo ja usa este header e
// funciona). O tema passou a mandar o header tambem, para que fechar a
// chave no backend vire UMA linha, sem tocar em tela de novo.
//
// getAuthHeaders() devolve {} sem token, entao nada muda para quem nao
// esta logado.
function getAuthHeaders() {
  const token = sessionStorage.getItem("access_token") || localStorage.getItem("access_token") || "";
  return token ? { Authorization: "Bearer " + token } : {};
}

// ── Motor de contraste automatico (WCAG) ──────────────────────────────────
function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map(c => c + c).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex({ r, g, b }) {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return "#" + c(r) + c(g) + c(b);
}
function luminanciaRelativa({ r, g, b }) {
  const norm = (v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}
function razaoContraste(corA, corB) {
  const la = luminanciaRelativa(corA), lb = luminanciaRelativa(corB);
  const claro = Math.max(la, lb), escuro = Math.min(la, lb);
  return (claro + 0.05) / (escuro + 0.05);
}
function misturar(corA, corB, t) {
  return { r: corA.r + (corB.r - corA.r) * t, g: corA.g + (corB.g - corA.g) * t, b: corA.b + (corB.b - corA.b) * t };
}
// Escolhe uma cor de texto segura: respeita a escolha do admin se o contraste
// contra o fundo for suficiente (>=4.5:1, padrao WCAG AA); caso contrario,
// ignora silenciosamente a escolha problematica e usa preto/branco, o que
// tiver maior contraste real contra o fundo.
function corTextoSegura(corFundoHex, corTextoHex) {
  const fundo = hexToRgb(corFundoHex) || { r: 15, g: 17, b: 23 };
  const desejada = hexToRgb(corTextoHex);
  if (desejada && razaoContraste(fundo, desejada) >= 4.5) return desejada;
  const branco = { r: 255, g: 255, b: 255 }, preto = { r: 0, g: 0, b: 0 };
  return razaoContraste(fundo, branco) >= razaoContraste(fundo, preto) ? branco : preto;
}
// Deriva uma camada de texto (secundaria/terciaria) misturando a cor de texto
// principal com o fundo, reduzindo a mistura automaticamente se o contraste
// resultante cair abaixo do minimo seguro (3:1) para aquela camada.
function derivarCamada(corTextoPrincipal, corFundo, misturaInicial, minimo) {
  let t = misturaInicial;
  while (t > 0) {
    const candidata = misturar(corTextoPrincipal, corFundo, t);
    if (razaoContraste(candidata, corFundo) >= minimo) return candidata;
    t -= 0.05;
  }
  return corTextoPrincipal;
}
// Correcao pontual -- vazamento visual entre tenants: se o login acontece
// via navegacao interna (SPA, sem reload completo da pagina -- ex.: sessao
// expira e cai em /login, ou o usuario troca de conta sem clicar "Sair"),
// as variaveis CSS aplicadas pelo tenant anterior ficavam no documento ate
// o novo tema carregar, e qualquer campo que o novo tema nao definisse
// continuava com a cor do tenant anterior. resetTemaPadrao() devolve tudo
// ao padrao de global.css antes de aplicar o tema do tenant atual.
function resetTemaPadrao() {
  const r = document.documentElement.style;
  ["--accent","--accent2","--bg2","--bg3","--bg1","--bg","--text","--text2","--text3","--font"].forEach(p => r.removeProperty(p));
  document.body.style.fontFamily = "";
  document.documentElement.setAttribute("data-estilo-fundo", "liso");
}
function aplicarTema(tema) {
  resetTemaPadrao();
  if (!tema) return;
  const r = document.documentElement.style;
  if (tema.corPrimaria)   { r.setProperty("--accent",  tema.corPrimaria); r.setProperty("--accent2", tema.corDestaque || tema.corPrimaria); }
  if (tema.corSecundaria) { r.setProperty("--bg2",     tema.corSecundaria); r.setProperty("--bg3", tema.corSecundaria); }
  if (tema.corFundo)      { r.setProperty("--bg1",     tema.corFundo); r.setProperty("--bg",  tema.corFundo); }
  if (tema.corFundo || tema.corTexto) {
    const fundoHex = tema.corFundo || "#0f1117";
    const fundoRgb = hexToRgb(fundoHex) || { r: 15, g: 17, b: 23 };
    const textoPrincipal = corTextoSegura(fundoHex, tema.corTexto);
    const textoSecundario = derivarCamada(textoPrincipal, fundoRgb, 0.35, 3);
    const textoTerciario  = derivarCamada(textoPrincipal, fundoRgb, 0.55, 2.2);
    r.setProperty("--text",  rgbToHex(textoPrincipal));
    r.setProperty("--text2", rgbToHex(textoSecundario));
    r.setProperty("--text3", rgbToHex(textoTerciario));
  }
  if (tema.fonte)         { r.setProperty("--font",    tema.fonte + ", sans-serif"); document.body.style.fontFamily = tema.fonte + ", sans-serif"; }
  document.documentElement.setAttribute("data-estilo-fundo", tema.estiloFundo || "liso");
}
export default function Layout({ children }) {
  const { t } = useTranslation();
  // Correcao pontual -- o overlay de "Sistema Bloqueado" cobria a tela
  // inteira, inclusive a rota /meu-plano, entao um tenant com licenca
  // expirada nao conseguia nem ver o botao "Assinar agora"/"Renovar agora"
  // para se auto-liberar (o proprio objetivo da automacao via Mercado Pago
  // -- ver services/mercadopago.py). O aviso (faixa vermelha no topo)
  // continua aparecendo em todas as paginas; so o backdrop que trava
  // cliques fica de fora nessa rota especifica.
  const location = useLocation();
  const emMeuPlano = location.pathname === "/meu-plano";
  // Correcao pontual -- flash residual ao trocar de tenant: useEffect roda
  // DEPOIS do navegador pintar a tela, entao mesmo resetando lá ainda dava
  // pra ver um frame com a cor do tenant anterior. useLayoutEffect roda
  // ANTES da pintura. Alem disso, o tema customizado da oficina so chegava
  // depois de um fetch de rede (assincrono por natureza), entao mesmo sem
  // vazamento do tenant anterior, dava pra ver o "pulo" do padrao pra cor
  // customizada assim que o fetch respondia. Agora o tema fica cacheado
  // (por tenant) e aplicado direto do cache, de forma sincrona, antes da
  // pintura -- o fetch so atualiza o cache/tela se algo tiver mudado.
  useLayoutEffect(() => {
    const tenantId = getTenantId();
    let cache = null;
    try { cache = JSON.parse(localStorage.getItem("saas_tema_tenant_" + tenantId) || "null"); } catch {}
    if (cache) aplicarTema(cache); else resetTemaPadrao();
  }, []);
  const [licencaStatus, setLicencaStatus] = React.useState(null);
  const [sistemaConfig, setSistemaConfig] = React.useState({email:"suporte@saas.gl",whatsapp:""});
  React.useEffect(() => {
    const tenantId = getTenantIdFromToken() || "1";
    // Carregar config do sistema (email/whatsapp suporte)
    fetch("" + API_BASE + "/api/v1/config/sistema")
      .then(r=>r.ok?r.json():null)
      .then(d=>{if(d&&d.value){setSistemaConfig(d.value);}})
      .catch(()=>{});
    fetch("" + API_BASE + "/api/v1/config/licenca_tenant_" + tenantId, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.value && d.value.dataExpiracao) {
          const exp = new Date(d.value.dataExpiracao);
          const hoje = new Date();
          const diffMs = exp - hoje;
          const dias = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          const minutos = Math.ceil(diffMs / (1000 * 60));
          const horas = Math.ceil(diffMs / (1000 * 60 * 60));
          const expirado = diffMs <= 0;
          setLicencaStatus({ dias, minutos, horas, expirado });
        }
      }).catch(() => {});
  }, []);
  const [recolhida, setRecolhida] = useState(false);
  const [config,    setConfig]    = useState(() => { try { return JSON.parse(localStorage.getItem(getConfigKey()) || "{}"); } catch { return {}; } });
  // Modo mobile -- abaixo do breakpoint a sidebar deixa de reservar espaco
  // no layout (marginLeft 0) e passa a se comportar como uma gaveta
  // deslizante sobreposta (ver Sidebar.js), aberta via botao hamburguer no
  // Header. w=0 aqui tambem zera a var CSS --sidebar-w usada pelo Header e
  // pelas faixas de aviso de licenca abaixo, entao tudo se realinha junto.
  const isMobile = useIsMobile();
  const [menuMobileAberto, setMenuMobileAberto] = useState(false);
  useEffect(() => { setMenuMobileAberto(false); }, [location.pathname]);
  const w = isMobile ? 0 : (recolhida ? 60 : 240);
  useEffect(() => {
    const tenantId = getTenantId();
    const carregarModulos = () => {
      Promise.all([
        fetch(CONFIG_API + "/modulos").then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(CONFIG_API + "/oficina_config_" + tenantId, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]).then(([modulosData, oficinaData]) => {
        const global = modulosData?.value || {};
        const perTenant = oficinaData?.value?.modulosAtivos;
        // AUD-E -- mesma resposta, campo que ja vinha junto.
        gravarSegmento(oficinaData?.value?.checklistConfig?.segmento);
        if (Array.isArray(perTenant) && perTenant.length > 0) {
          setConfig(prev => {
            const next = { ...prev, modulos: perTenant, modulosLabels: global };
            try { localStorage.setItem(getConfigKey(), JSON.stringify(next)); } catch {}
            return next;
          });
        } else if (modulosData?.value) {
          const ativos = Object.entries(global)
            .filter(([_, m]) => m.ativo)
            .map(([k]) => k);
          setConfig(prev => {
            const next = { ...prev, modulos: ativos, modulosLabels: global };
            try { localStorage.setItem(getConfigKey(), JSON.stringify(next)); } catch {}
            return next;
          });
        }
      });
    };
    carregarModulos();
    const onConfig = () => carregarModulos();
    window.addEventListener("config_atualizado", onConfig);
    // Carrega tema da oficina (o useLayoutEffect acima ja aplicou a versao
    // em cache -- isto so atualiza se algo mudou desde a ultima visita).
    fetch(CONFIG_API + "/tema_oficina_" + tenantId, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.value) {
          aplicarTema(d.value);
          try { localStorage.setItem("saas_tema_tenant_" + tenantId, JSON.stringify(d.value)); } catch {}
          setConfig(prev => {
            // Correcao pontual -- foto de perfil "sumindo e aparecendo" na
            // troca de sessao: essa atualizacao nunca persistia no cache
            // (so carregarModulos() persistia), entao o avatarUrl so ficava
            // disponivel depois da resposta de rede -- ate la, o Sidebar
            // mostrava o icone generico no lugar da foto. Agora persiste
            // aqui tambem, igual ja acontece com modulos.
            const next = {
              ...prev,
              nome:      d.value.logoTexto     || prev.nome,
              logo:      d.value.logoUrl       || prev.logo,
              subtitulo: d.value.logoSubtitulo || prev.subtitulo,
              avatarUrl: d.value.avatarUrl     || prev.avatarUrl,
            };
            try { localStorage.setItem(getConfigKey(), JSON.stringify(next)); } catch {}
            return next;
          });
        } else {
          // Tenant sem tema customizado (ou removeu o que tinha) -- limpa
          // um cache antigo pra nao ficar aplicando uma cor que nao existe
          // mais na proxima troca de sessao.
          try { localStorage.removeItem("saas_tema_tenant_" + tenantId); } catch {}
          resetTemaPadrao();
        }
      })
      .catch(() => {});
    // Carrega dados da oficina (nome, filial)
    // AUD-001: esta chamada era a unica do painel que ia sem token, e era
    // por causa dela que GET /tenants/{id} continuava publico (ver o
    // comentario em routes/tenant.py). getAuthHeaders() ja e usada duas
    // vezes neste mesmo efeito, logo acima; devolve {} quando nao ha
    // token, entao nenhum cenario piora.
    fetch(TENANT_API + "/" + tenantId, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setConfig(prev => ({
            ...prev,
            filial: d.name || d.nome || prev.filial,
          }));
        }
      })
      .catch(() => {});
    return () => window.removeEventListener("config_atualizado", onConfig);
  }, []);
  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <style>{`:root { --sidebar-w: ${w}px; }`}</style>
      {/* Mesma nebulosa azul de fundo da landing/login -- sem isso o vidro
          da sidebar (backdrop-filter) nao tinha nada colorido atras pra
          borrar, entao ficava com aparencia solida/chapada em vez de
          luminosa. zIndex:-1 e pointer-events:none, nao interfere em nada. */}
      <Nebulosa />
      <EstiloGlobal />
      <LembretesLaterais />
      <Sidebar
        config={config}
        recolhida={recolhida}
        setRecolhida={setRecolhida}
        licencaStatus={licencaStatus}
        isMobile={isMobile}
        mobileOpen={menuMobileAberto}
        onCloseMobile={() => setMenuMobileAberto(false)}
      />
      <div style={{ flex: 1, marginLeft: w }}>
        <Header config={config} isMobile={isMobile} onMenuClick={() => setMenuMobileAberto(true)} />
        {licencaStatus && licencaStatus.expirado && (
          <>
            <div style={{background:"#ef4444",color:"#fff",fontSize:14,fontWeight:700,padding:"14px 24px",position:"fixed",top:60,left:"var(--sidebar-w)",right:0,zIndex:9999,textAlign:"center"}}>
              🔒 {t("layout.licenca_expirada_banner")}
            </div>
            {!emMeuPlano && (
              <div style={{alignItems:"center",background:"#0f172acc",bottom:0,display:"flex",flexDirection:"column",justifyContent:"center",left:"var(--sidebar-w)",position:"fixed",right:0,top:100,zIndex:9998}}>
                <div style={{background:"#131929",border:"2px solid #ef4444",borderRadius:16,maxWidth:460,padding:40,textAlign:"center"}}>
                  <div style={{fontSize:48,marginBottom:16}}>🔒</div>
                  <div style={{color:"#ef4444",fontSize:22,fontWeight:800,marginBottom:8}}>{t("layout.sistema_bloqueado")}</div>
                  <div style={{color:"#94a3b8",fontSize:14,marginBottom:24}}>{t("layout.licenca_expirou_desc")}</div>
                  <div style={{color:"#64748b",fontSize:12}}>{t("layout.suporte")}: {sistemaConfig.email||"suporte@saas.gl"}</div>{sistemaConfig.whatsapp&&<a href={"https://wa.me/"+sistemaConfig.whatsapp} target="_blank" rel="noreferrer" style={{color:"#22c55e",fontSize:12,display:"block",marginTop:4}}>WhatsApp: {sistemaConfig.whatsapp}</a>}
                </div>
              </div>
            )}
          </>
        )}
        {licencaStatus && !licencaStatus.expirado && licencaStatus.dias <= 7 && (
          <div style={{background:licencaStatus.minutos<=60?"#ef4444":licencaStatus.horas<=24?"#f97316":"#f59e0b",color:"#fff",fontSize:13,fontWeight:600,padding:"10px 24px",position:"fixed",top:60,left:"var(--sidebar-w)",right:0,zIndex:98,textAlign:"center"}}>
            ⚠️ {licencaStatus.minutos<=60
              ? t("layout.expira_em_minutos",{count:licencaStatus.minutos})
              : licencaStatus.horas<=24
                ? t("layout.expira_em_horas",{count:licencaStatus.horas})
                : t("layout.expira_em_dias",{count:licencaStatus.dias})}{" "}{t("layout.entre_em_contato_renovar")}
          </div>
        )}
        <main style={{
          padding: isMobile
            ? (licencaStatus && (licencaStatus.expirado || licencaStatus.dias <= 7) ? "116px 14px 24px" : "72px 14px 24px")
            : (licencaStatus && (licencaStatus.expirado || licencaStatus.dias <= 7) ? "112px 24px 24px" : "76px 24px 24px"),
        }}>
          <div className="fade-up">{children}</div>
        </main>
      </div>
    </div>
  );
}
